import { Dispatch, SetStateAction, useRef, useState } from "react";
import { IonCheckbox, IonContent, IonInput, IonItem, IonList, IonModal } from "@ionic/react";
import { SelectItem } from "components/FormFields/SelectItem";
import { TeacherKind } from "model";
import { Colors } from "swing-components";

import { translate } from "~utils";

import { ModalHeader } from "./ModalHeader";
import { RadioButton } from "./RadioButton";

type TeacherObject = {
  id: string;
  name: string;
  email: string;
};

type TeacherFieldProps = {
  value: string;
  teacherKind: TeacherKind;
  setTeacherKind: Dispatch<SetStateAction<TeacherKind | string>>;
  teachers: TeacherObject[];
  onChange: (v: TeacherKind) => void;
  setTeacherId: Dispatch<SetStateAction<string>>;
  isDisabled?: boolean;
};

type TeacherFieldModalProps = TeacherFieldProps & {
  trigger: string;
};

export function isRovingTeacher(value: TeacherKind) {
  return value === "Roving";
}

export function TeacherField(props: TeacherFieldProps) {
  const { value, isDisabled } = props;

  return (
    <SelectItem
      id="open-teacher-modal"
      label="Staff"
      value={value}
      isDisabled={isDisabled}
      css={isDisabled ? { color: Colors.slate300, cursor: "not-allowed" } : undefined}
    >
      <TeacherFieldModal trigger="open-teacher-modal" {...props} />
    </SelectItem>
  );
}

const TeacherFieldModal = (props: TeacherFieldModalProps) => {
  const { trigger, teacherKind, setTeacherKind, onChange, teachers, setTeacherId, value } = props;

  const teacherModal = useRef<HTMLIonModalElement>(null);
  const [teacherName, setTeacherName] = useState(value);
  const [showEmail, setShowEmail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const onModalConfirm = () => {
    switch (teacherKind) {
      case "Roving": {
        onChange("Roving");
        break;
      }
      case "Vacancy": {
        onChange((teacherName as TeacherKind) || "Vacancy");
        break;
      }
      default: {
        const teacherObj = teachers.find((teacher) => teacher.name === teacherName);
        setTeacherId(teacherObj?.id || "");
        onChange(teacherName as TeacherKind);
      }
    }
    teacherModal.current?.dismiss();
  };

  return (
    <IonModal
      trigger={trigger}
      ref={teacherModal}
      backdropDismiss={false}
      className="modal-600 stackedModal"
    >
      <ModalHeader
        title={translate("TEACHER_MODAL_HEADER")}
        onModalConfirm={onModalConfirm}
        onIonInput={teachers?.length > 0 ? (e) => setSearchTerm(e?.target?.value || "") : undefined}
      />
      <IonContent>
        <IonList>
          {teachers?.length > 0 && (
            <IonItem lines="none">
              <IonCheckbox
                justify="end"
                checked={showEmail}
                onClick={() => setShowEmail((old) => !old)}
              >
                Show emails
              </IonCheckbox>
            </IonItem>
          )}

          <RadioButton
            checked={teacherKind === "Roving"}
            onChange={() => {
              setTeacherKind("Roving");
            }}
          >
            {translate("TEACHER_LABEL_ROVING")}
          </RadioButton>
          <RadioButton
            checked={teacherKind === "Vacancy"}
            onChange={() => {
              setTeacherName("");
              setTeacherKind("Vacancy");
            }}
          >
            {translate("TEACHER_LABEL_VACANCY")}
          </RadioButton>
          {teacherKind === "Vacancy" && (
            <IonItem>
              <IonInput
                value={teacherName || ""}
                onIonInput={(e) => {
                  setTeacherName(e.target.value?.toString() || "");
                }}
                placeholder={translate("TEACHER_INPUT_PLACEHOLDER")}
              />
            </IonItem>
          )}
          {teachers?.length > 0 &&
            teachers
              .filter((teacher: TeacherObject) =>
                teacher.name.toLowerCase().includes(searchTerm.toLowerCase()),
              )
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((teacher) => (
                <RadioButton
                  value={teacher.name}
                  key={teacher.id}
                  checked={teacherKind === teacher.name}
                  onChange={(e) => {
                    setTeacherKind(e.target.value as TeacherKind);
                    setTeacherName(e.target.value);
                  }}
                >
                  {teacher.name} {showEmail && `(${teacher.email})`}
                </RadioButton>
              ))}
        </IonList>
      </IonContent>
    </IonModal>
  );
};
